import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import type { IPlanWriteAck } from '@apis/schedule';
import { scheduleKey } from '@constants/queryKeys';
import type { IRootState } from '@store/slices';
import type { ISchedulerData, ISchedulerTask, SiteId } from '@interfaces/IScheduler';
import type { IImmediateWriteContext } from './useImmediateWritePersist';
import { usePullPlan } from './usePullPlan';
import { useSchedulerActions } from './useSchedulerStore';

/**
 * Send one of this client's per-task PLAN writes and settle the version it
 * produced. `request` performs the write and answers with the schedule's
 * version after it and whether this call moved it; `apply` is the write
 * mirrored onto one saved-baseline task.
 */
export type OwnPlanWriteAck = (
  ctx: IImmediateWriteContext,
  request: () => Promise<IPlanWriteAck>,
  apply: (task: ISchedulerTask) => ISchedulerTask,
) => Promise<void>;

// ---------------------------------------------------------------------------
// useOwnPlanWriteAck — acknowledge the schedule version our own per-task plan
// write produced (the drawer's auto-saved fields, the row actions' status).
//
// Every plan write bumps the version. Left alone, the stale probe would read
// our own bump as another editor's Save and freeze the page. When the answer
// is exactly one past the version the store holds and this call wrote it, the
// write was the only one — the store's token and the query-cache baseline just
// move up to it, and the user's other unsaved edits and the rest of the page
// are left alone. Any other answer means another editor wrote in between, and
// `usePullPlan` rebases.
// ---------------------------------------------------------------------------
export function useOwnPlanWriteAck(siteId: SiteId): OwnPlanWriteAck {
  const qc = useQueryClient();
  const store = useStore<IRootState>();
  const { planVersionAcknowledged } = useSchedulerActions();
  const pullPlan = usePullPlan(siteId);

  return useCallback(
    async (ctx, request, apply) => {
      const held = Number(store.getState().scheduler.bySite[ctx.siteId]?.version);
      const { version: versionStr, wrote } = await request();
      const version = Number(versionStr);
      // `wrote` says whether THIS call moved the version — never infer that
      // from the version number alone. A no-op can land on `held + 1` by
      // coincidence when a concurrent editor wrote first; acking that as
      // this call's write would cache the held baseline plus our own edit
      // over their content, and a later toolbar Save (CAS on the version we
      // just acked) would overwrite it for good.
      if (!wrote) {
        if (version === held) return; // truly nothing changed anywhere
        await pullPlan(ctx.scheduleNumber); // someone else wrote; rebase
        return;
      }
      if (version !== held + 1) {
        // We wrote, but not from the version we held — another editor's
        // write landed first. Rebase rather than ack a version that isn't
        // ours to claim.
        await pullPlan(ctx.scheduleNumber);
        return;
      }
      qc.setQueryData<ISchedulerData>(scheduleKey(ctx.siteId, ctx.scheduleNumber), (baseline) =>
        baseline && {
          ...baseline,
          version: String(version),
          tasks: baseline.tasks.map((task) => (task.id === ctx.taskId ? apply(task) : task)),
        },
      );
      planVersionAcknowledged({ siteId: ctx.siteId, version: String(version) });
    },
    [store, qc, pullPlan, planVersionAcknowledged],
  );
}
